import './federation-guard';
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';
import VueVirtualScroller from 'vue-virtual-scroller';
import scrollerStyles from 'vue-virtual-scroller/dist/vue-virtual-scroller.css?inline';
import { FocusTrap } from 'vue-focus-guard';
import i18n from '@/i18n';
import { useI18n } from 'vue-i18n';
import { createBootstrap, vBTooltip } from 'bootstrap-vue-next';
import baseStyles from 'ui-component-library/assets/styles/main.scss?inline';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';

// The standalone app's document-wide styles, injected here instead of being
// emitted as a stylesheet: the federation build ships every emitted
// stylesheet with its exposed module (`bundleAllCSS`), and these would restyle
// a host console. Embedded, the same sheets come scoped (assets/styles/embedded.scss).
for (const css of [baseStyles, scrollerStyles]) {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.append(style);
}

const pinia = createPinia();
const app = createApp(App, {
  setup() {
    const { t } = useI18n();
    return { t };
  },
});

app.use(pinia);
app.use(router);
app.use(VueVirtualScroller);
app.component('FocusTrap', FocusTrap);
app.directive('b-tooltip', vBTooltip);
app.use(i18n);
app.use(
  createBootstrap({
    components: {
      BTooltip: {
        delay: {
          show: 0,
          hide: 0,
        },
        teleportTo: 'body',
        noninteractive: false,
        trigger: 'hover',
        boundaryPadding: 5,
      },
    },
  })
);

const blurElementTooltip = (element: Partial<HTMLElement>) => {
  const isElementTooltip = element.classList.contains('tooltip');
  if (isElementTooltip) {
    element.blur();
  } else if (element.parentElement) {
    blurElementTooltip(element.parentElement);
  }
};

document.addEventListener('click', (event: Event) => {
  blurElementTooltip(event.target);
});

app.mount('#app');

type AppTranslate = {
  (key: string): string;
  (key: string, named: Record<string, unknown>): string;
  (key: string, plural: number): string;
  (key: string, plural: number, named: Record<string, unknown>): string;
  (key: string, list: unknown[]): string;
  (key: string, plural: number, list: unknown[]): string;
};

declare module '@vue/runtime-core' {
  export interface ComponentCustomProperties {
    $refs: {
      [key: string]: any;
    };
    $t: AppTranslate;
    $router: Router;
    $route: RouteLocationNormalizedLoaded;
  }
}
